<script setup>
import TabContainer from "@/components/global-themed/tabs/TabContainer.vue";
import TabItem from "@/components/global-themed/tabs/TabItem.vue";
import {computed, ref} from "vue";
import ConfirmationModal from "@/components/settings/notifications/modals/ConfirmationModal.vue";
import {kitName} from "@/composables/useLocalDomains";
import useUser from "@/composables/useUser";
import useApprovalSettings from "@/composables/approval-center/settings/useApprovalSettings";
import WeeklyDayPicker from "@/components/settings/notifications/WeeklyDayPicker.vue";
import AccordionTransition from "@/components/transitions/AccordionTransition.vue";

const {isActiveWebsiteAgency} = useUser();

const pendingSelection = ref(null);

const {settingsQuery, saveFrequencyMutation, websiteWeeklyDaySetting} = useApprovalSettings();
const {mutate: saveFrequency, isPending} = saveFrequencyMutation;

const emailFrequencySettingData = computed(() => settingsQuery.data.value?.website_email_setting || []);
const emailFrequencySetting = computed(() => {
  if (emailFrequencySettingData.value?.website_setting) {
    return emailFrequencySettingData.value.website_setting;
  }

  return 'agency_default';
});

const agencyDefaultLabel = computed(() => {
  const orgSetting = emailFrequencySettingData.value?.organisation_setting;
  return orgSetting ? `Agency Default: ${orgSetting.charAt(0).toUpperCase() + orgSetting.slice(1)}` : 'Agency Default';
});

const frequencyOptions = computed(() => {
  if (isActiveWebsiteAgency.value) {
    return [
      {label: agencyDefaultLabel.value, value: "agency_default"},
      {label: "Real-time", value: "realtime"},
      {label: "Off", value: "off"},
    ];
  }

  return  [
    {label: "Real-time", value: "realtime"},
    {label: "Daily", value: "daily"},
    {label: "Weekly", value: "weekly"},
    {label: "Off", value: "off"},
  ];
});

const confirmationFrequencies = computed(() => {
  if (isActiveWebsiteAgency.value) {
    return new Set(["off", "realtime"]);
  }

  return new Set(["off", "weekly", 'daily']);
});

const needsConfirmation = (frequency) => confirmationFrequencies.value.has(frequency);

const confirmationCopy = computed(() => {
  if (isActiveWebsiteAgency.value) {
    return {
      off: {
        heading: "Turn Off Emails for This Website?",
        text: "You won't receive any emails for updates on this website. This overrides your agency-level settings. This website will not be included in any daily or weekly summary emails. Updates will still be available in the platform.",
        subtext: "Are you sure you want to turn off emails for this website?",
        confirmLabel: "Turn off emails",
        cancelLabel: "Cancel",
        state: "sad",
      },
      realtime: {
        heading: "Enable Real-Time Emails for This Website?",
        text: "You'll receive an email as soon as new updates are ready for approval on this website. This overrides your agency-level settings. This website will not be included in any daily or weekly summary emails.",
        subtext: "Are you sure you want to enable real-time emails?",
        confirmLabel: "Enable real-time emails",
        cancelLabel: "Cancel",
        state: "default",
      },
    };
  }

  return {
    off: {
      heading: "Turn Off Emails for This Website?",
      text: "You won't receive any emails for updates on this website. Updates will still be available in the platform.",
      subtext: "Are you sure you want to turn off emails for this website?",
      confirmLabel: "Turn off emails",
      cancelLabel: "Cancel",
      state: "sad",
    },
    daily: {
      heading: "Switch to Daily Email Summary?",
      text: "All updates will be grouped into a single daily email, so you can review everything ready for approval in one place.",
      subtext: "Are you sure you want to switch to daily emails?",
      confirmLabel: "Enable daily emails",
      cancelLabel: "Cancel",
      state: "default",
    },
    weekly: {
      heading: "Switch to Weekly Email Summary?",
      text: "All updates will be grouped into a single weekly email, sent on your selected day, so you can review everything in one place.",
      subtext: "Are you sure you want to switch to weekly emails?",
      confirmLabel: "Enable weekly emails",
      cancelLabel: "Cancel",
      state: "default",
    },
  };
})

const handleFrequencySelection = (frequency) => {
  if (needsConfirmation(frequency)) {
    pendingSelection.value = frequency;
    return;
  }

  if (isPending.value) {
    return;
  }

  submitChange(frequency)
}

const getDefaultDay = () => {
  const today = new Date().getDay();

  if (today === 0 || today === 6) {
    return 1;
  }

  return today;
};

const dayOfWeekSelection = computed({
  get: () => websiteWeeklyDaySetting.value ?? getDefaultDay(),
  set: (value) => {
    if (isPending.value) {
      return;
    }

    submitChange('weekly', value);
  },
});

const submitChange = (frequency, weeklyDay = null) => {
  if (isPending.value) {
    return;
  }

  const payload = {email_frequency: frequency};

  if (frequency === 'weekly') {
    payload.weekly_day = weeklyDay ?? dayOfWeekSelection.value;
  }

  saveFrequency(payload);
}

const modalCopy = computed(() => {
  if (!pendingSelection.value) {
    return null;
  }

  return confirmationCopy.value[pendingSelection.value];
});

const closeModal = () => {
  pendingSelection.value = null;
};

const confirmChange = () => {
  if (!pendingSelection.value) {
    return;
  }

  submitChange(pendingSelection.value);
  pendingSelection.value = null;
};
</script>

<template>
  <div class="tw-bg-white tw-rounded-lg tw-p-8 tw-flex tw-flex-col tw-gap-8">
    <div class="tw-flex tw-flex-col tw-gap-6">
      <div class="tw-flex tw-justify-between tw-items-center">
        <p class="tw-font-bold tw-text-neutral-900 tw-text-2.5xl">
          Website Email Notifications
        </p>

        <TabContainer
          class="tw-grid tw-gap-2"
          :class="[
            isActiveWebsiteAgency ? 'tw-grid-cols-3' : 'tw-grid-cols-4'
          ]"
        >
          <TabItem
            v-for="option in frequencyOptions"
            :key="option.value"
            :active="emailFrequencySetting === option.value"
            :class="[
              'tw-justify-center',
              {
                'tw-pointer-events-none': isPending,
              }
            ]"
            class="tw-justify-center"
            @click="handleFrequencySelection(option.value)"
          >
            <span>{{ option.label }}</span>
          </TabItem>
        </TabContainer>
      </div>

      <p class="tw-text-neutral-900 tw-text-base tw-leading-snug">
        <template v-if="isActiveWebsiteAgency">
          Choose how this website receives email notifications when {{ kitName }} completes work that requires approval. By default, this website follows your <a
            class="tw-text-primary"
            href="/settings#notifications"
            target="_blank"
          >agency notification settings</a>, but you can override this for specific websites if needed. For example, you may want high-priority clients to receive real-time notifications while others follow your agency summary schedule.
        </template>
        <template v-else>
          Choose how this website receives email notifications when updates are ready for approval. You can receive emails in real time, as a daily or weekly summary, or turn them off completely.
        </template>
      </p>
    </div>

    <AccordionTransition>
      <div
        v-if="!isActiveWebsiteAgency && emailFrequencySetting === 'weekly'"
        class="tw-transition-all tw-duration-300 tw-ease-in-out"
      >
        <WeeklyDayPicker
          v-model="dayOfWeekSelection"
          :disabled="isPending"
        />
      </div>
    </AccordionTransition>
  </div>

  <ConfirmationModal
    v-if="pendingSelection && modalCopy"
    :state="modalCopy.state"
    :confirm-label="modalCopy.confirmLabel"
    :cancel-label="modalCopy.cancelLabel"
    @close="closeModal"
    @confirm="confirmChange"
  >
    <template #heading>
      {{ modalCopy.heading }}
    </template>
    <template #text>
      {{ modalCopy.text }}
    </template>
    <template #subtext>
      {{ modalCopy.subtext }}
    </template>
  </ConfirmationModal>
</template>
